/**
 * Blog Page
 * Route: /blog
 * Features: Featured article hero, category filters, article grid, newsletter signup
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Calendar,
  Clock,
  Newspaper,
  Search,
  Sparkles,
  Tag,
  TrendingUp,
  Mail,
  ChevronRight,
} from 'lucide-react';
import { blogPosts, blogCategories, type BlogPost } from '@/data/blogData';

/* ─── Category Icon map ─── */
const categoryIcons: Record<string, React.ReactNode> = {
  All: <Newspaper className="h-3.5 w-3.5" />,
  'Selling Tips': <TrendingUp className="h-3.5 w-3.5" />,
  Safety: <Sparkles className="h-3.5 w-3.5" />,
  'Market Trends': <TrendingUp className="h-3.5 w-3.5" />,
  'Success Stories': <BookOpen className="h-3.5 w-3.5" />,
  'Product Updates': <Sparkles className="h-3.5 w-3.5" />,
};

/* ─── Format Date ─── */
const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

/* ─── Featured Article Card ─── */
const FeaturedArticle: React.FC<{ post: BlogPost }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    id="featured-article"
    className="blog-featured group"
  >
    {/* Gradient Cover */}
    <div
      className="blog-featured__cover"
      style={{ background: post.coverGradient }}
    >
      <div className="blog-featured__cover-pattern" />
      <div className="blog-featured__cover-badge">
        <Sparkles className="h-3.5 w-3.5" />
        Featured Article
      </div>
    </div>

    {/* Content */}
    <div className="blog-featured__body">
      <div className="blog-featured__meta">
        <span className="blog-featured__category">{post.category}</span>
        <span className="blog-featured__dot">·</span>
        <span className="blog-featured__date">
          <Calendar className="h-3 w-3" /> {formatDate(post.publishedAt)}
        </span>
        <span className="blog-featured__dot">·</span>
        <span className="blog-featured__read-time">
          <Clock className="h-3 w-3" /> {post.readTime} min read
        </span>
      </div>
      <h2 className="blog-featured__title">{post.title}</h2>
      <p className="blog-featured__excerpt">{post.excerpt}</p>
      <div className="blog-featured__footer">
        <div className="blog-featured__author">
          <div className="blog-featured__avatar">{post.author.avatar}</div>
          <div>
            <span className="blog-featured__author-name">{post.author.name}</span>
            <span className="blog-featured__author-role">{post.author.role}</span>
          </div>
        </div>
        <span className="blog-featured__cta group-hover:gap-3">
          Read Article <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  </Link>
);

/* ─── Article Card ─── */
const ArticleCard: React.FC<{ post: BlogPost; index: number }> = ({ post, index }) => (
  <Link
    to={`/blog/${post.slug}`}
    id={`blog-card-${post.id}`}
    className="blog-card group"
    style={{ animationDelay: `${index * 80}ms` }}
  >
    {/* Gradient Cover */}
    <div
      className="blog-card__cover"
      style={{ background: post.coverGradient }}
    >
      <div className="blog-card__cover-pattern" />
      <div className="blog-card__read-time-badge">
        <Clock className="h-3 w-3" />
        {post.readTime} min
      </div>
    </div>

    {/* Content */}
    <div className="blog-card__body">
      <div className="blog-card__meta">
        <span className="blog-card__category">{post.category}</span>
        <span className="blog-card__date">{formatDate(post.publishedAt)}</span>
      </div>
      <h3 className="blog-card__title">{post.title}</h3>
      <p className="blog-card__excerpt">{post.excerpt}</p>
      <div className="blog-card__footer">
        <div className="blog-card__author">
          <div className="blog-card__avatar">{post.author.avatar}</div>
          <span className="blog-card__author-name">{post.author.name}</span>
        </div>
        <ChevronRight className="blog-card__arrow h-4 w-4" />
      </div>
    </div>
  </Link>
);

/* ─── Newsletter Section ─── */
const Newsletter: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <section className="blog-newsletter" id="blog-newsletter">
      <div className="blog-newsletter__inner">
        <div className="blog-newsletter__icon-wrap">
          <Mail className="h-6 w-6" />
        </div>
        <h3 className="blog-newsletter__title">Stay Ahead of the Market</h3>
        <p className="blog-newsletter__desc">
          Get weekly insights on selling smarter, buying safer, and marketplace trends
          delivered straight to your inbox.
        </p>
        {subscribed ? (
          <div className="blog-newsletter__success">
            <Sparkles className="h-4 w-4" />
            You're subscribed! Check your inbox for a welcome email.
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="blog-newsletter__form">
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="blog-newsletter__input"
              id="newsletter-email"
            />
            <button type="submit" className="blog-newsletter__btn" id="newsletter-subscribe">
              Subscribe
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}
        <p className="blog-newsletter__disclaimer">No spam, unsubscribe anytime. Read our <Link to="/privacy" className="blog-newsletter__link">Privacy Policy</Link>.</p>
      </div>
    </section>
  );
};

/* ─── Main Blog Page ─── */
const BlogPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const featuredPost = blogPosts.find((p) => p.featured)!;

  const filteredPosts = useMemo(() => {
    return blogPosts
      .filter((p) => !p.featured)
      .filter((p) => activeCategory === 'All' || p.category === activeCategory)
      .filter(
        (p) =>
          !searchQuery ||
          p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
      );
  }, [activeCategory, searchQuery]);

  return (
    <div className="blog-page" id="blog-page">
      <SEO
        title="Blog"
        description="Stay updated with the latest selling guides, safety advice, success stories, and marketplace tips on the MemAds Blog."
        canonical="/blog"
      />
      {/* ── Page Header ── */}
      <section className="blog-header">
        <div className="blog-header__inner">
          <Link
            to="/"
            className="blog-header__back"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <div className="blog-header__title-row">
            <div className="blog-header__icon-wrap">
              <BookOpen className="h-6 w-6" />
            </div>
            <div>
              <h1 className="blog-header__title">MemAds Blog</h1>
              <p className="blog-header__subtitle">
                Expert insights on buying, selling, and thriving in India's largest marketplace
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="blog-header__search-wrap">
            <Search className="blog-header__search-icon h-4 w-4" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="blog-header__search-input"
              id="blog-search"
            />
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <div className="blog-content">
        {/* Category Pills */}
        <div className="blog-categories" id="blog-categories">
          {blogCategories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setActiveCategory(cat.name)}
              className={`blog-categories__pill ${activeCategory === cat.name ? 'blog-categories__pill--active' : ''}`}
              id={`blog-cat-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {categoryIcons[cat.name]}
              {cat.name}
              <span className="blog-categories__count">{cat.count}</span>
            </button>
          ))}
        </div>

        {/* Featured Article (only when showing "All" category and no search) */}
        {activeCategory === 'All' && !searchQuery && (
          <FeaturedArticle post={featuredPost} />
        )}

        {/* Article Grid */}
        {filteredPosts.length > 0 ? (
          <div className="blog-grid" id="blog-grid">
            {filteredPosts.map((post, i) => (
              <ArticleCard key={post.id} post={post} index={i} />
            ))}
          </div>
        ) : (
          <div className="blog-empty" id="blog-empty">
            <Search className="h-10 w-10 text-muted-foreground/40" />
            <h3 className="blog-empty__title">No articles found</h3>
            <p className="blog-empty__desc">
              Try adjusting your search or browse a different category.
            </p>
            <button
              onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}
              className="blog-empty__reset"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Tags Cloud */}
        <section className="blog-tags" id="blog-tags">
          <h3 className="blog-tags__title">
            <Tag className="h-4 w-4" />
            Popular Topics
          </h3>
          <div className="blog-tags__cloud">
            {Array.from(new Set(blogPosts.flatMap((p) => p.tags))).map((tag) => (
              <button
                key={tag}
                onClick={() => setSearchQuery(tag)}
                className="blog-tags__tag"
              >
                {tag}
              </button>
            ))}
          </div>
        </section>

        {/* Newsletter */}
        <Newsletter />
      </div>
    </div>
  );
};

export default BlogPage;
